import React, { useState, useEffect, useMemo, useRef } from "react";
import { fetchModels } from "components/main/utils/models/ModelServices";
import { ModelElementType } from "components/entities";
import { createLogger } from "debug/DebugingLogger";
import {
  fetchTransferDestinations,
  handleTableOwnershipTransfer,
  type TransferDestination,
} from "@/components/canvas/detailsSide/tableDetails/TableServices";
import { DeleteDialogSection } from "components/main/utils/models/deletion/DeleteDialogSection";
import { DeleteDialogItem } from "components/main/utils/models/deletion/DeleteDialogItem";
import { AssetIcon } from "components/common/AssetIcon";
import { SearchBar } from "components/common/SearchBar";
import { StatusChip } from "components/common/StatusChip";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import { TooltipButton } from "components/common/TooltipButton";
import { showNotificationGlobal } from "stores/useNotificationStore";
import { Spinner } from "components/common/Spinner";
import { Stack } from "components/common/Stack";
import { ConfirmDialog } from "components/main/utils/ConfirmDialog";
import { removeFromSet, addToSet } from "components/common/setUtils";

const log = createLogger("Ownership Transfer – My Models");

/// Why a destination is refused, in the words the person reading it needs. Keyed
/// by the server's `reason`, so a new refusal at the door shows up here as a
/// missing key rather than a silently generic tooltip.
const REFUSAL_COPY: Record<
  NonNullable<TransferDestination["reason"]>,
  string
> = {
  org_boundary:
    "This table belongs to an organization, so it can't be moved outside of it",
  sealed: "This table is in a sealed space — its data can't leave that space",
  catalog_direction: "A catalog table can only move to another catalog model",
  parent_lock:
    "External-source tables can't be moved into the catalog. Their data stays with the credentials that connected it",
  not_manager: "You need to manage this model's space to move a table into it",
};

/// Refusals about the DESTINATION rather than about this table are dropped, not
/// greyed: from a company-org model every personal model comes back
/// `org_boundary`, and a wall of disabled rows is noise. The rest are facts
/// about the table and refuse nearly every row at once — hiding those would
/// empty the dialog with no explanation.
const HIDDEN_REFUSALS: ReadonlySet<NonNullable<TransferDestination["reason"]>> =
  new Set(["org_boundary"]);

const ActionStatus = ({
  isLoading,
  isSuccess,
  isFailed,
  onAction,
  disabled,
  title = "Move table here",
}: {
  isLoading: boolean;
  isSuccess: boolean;
  isFailed: boolean;
  onAction: (e: React.MouseEvent) => void;
  disabled: boolean;
  title?: string;
}) => {
  const showIcon = isLoading || isSuccess || isFailed;
  return (
    <div
      className={`transition-opacity flex items-center shrink-0 ${showIcon ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {isLoading ? (
        <Spinner />
      ) : isSuccess ? (
        <CheckCircleOutlineIcon className="icon-sm text-success" />
      ) : isFailed ? (
        <CancelOutlinedIcon className="icon-sm text-error" />
      ) : (
        <TooltipButton
          title={title}
          aria-label={title}
          variant="contextual"
          size="xs"
          onClick={(e) => {
            e.stopPropagation();
            onAction(e);
          }}
          disabled={disabled}
        >
          <SendOutlinedIcon />
        </TooltipButton>
      )}
    </div>
  );
};

interface MyModelsTabProps {
  currentModelId: string | undefined;
  currentModelIsCatalog: boolean;
  /// Composed FGA `can_transfer` — whether the table's data may leave the
  /// caller's own space. Required to move into a catalog model.
  canEnterCatalog: boolean;
  tableId: string;
  tableName: string;
  open: boolean;
  onClose: () => void;
}

export const MyModelsTab: React.FC<MyModelsTabProps> = ({
  currentModelId,
  currentModelIsCatalog,
  canEnterCatalog,
  tableId,
  tableName,
  open,
  onClose,
}) => {
  const [models, setModels] = useState<ModelElementType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [transferringModelId, setTransferringModelId] = useState<string | null>(
    null,
  );
  const [successModelIds, setSuccessModelIds] = useState<Set<string>>(
    new Set(),
  );
  const [failedModelIds, setFailedModelIds] = useState<Set<string>>(new Set());
  const [eligibility, setEligibility] = useState<
    Record<string, TransferDestination>
  >({});
  const [eligibilityPending, setEligibilityPending] = useState(false);
  const [pendingDetach, setPendingDetach] = useState<ModelElementType | null>(
    null,
  );
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    return () => {
      timersRef.current.forEach(clearTimeout);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    setIsLoading(true);
    setSearchQuery("");
    setTransferringModelId(null);
    setSuccessModelIds(new Set());
    setFailedModelIds(new Set());
    setPendingDetach(null);

    // Not `owned_only`: that filters on relation Owner, and a catalog model has
    // no owner — every manager arrives as Collaborator.
    //
    // UNSCOPED (D42): a personal-org table may move into any model the caller
    // manages, in any org, so narrowing to the active org context would hide
    // exactly the destinations the door allows. The server then judges each
    // candidate, so listing more never means offering more.
    fetchModels(undefined, false, { unscoped: true })
      .then(setModels)
      .catch((error) => {
        log.error("Failed to fetch models", error);
        setModels([]);
      })
      .finally(() => setIsLoading(false));
  }, [open]);

  // A catalog table stays in the catalog — the server refuses catalog → personal
  // with 403 catalog_table_stays_in_catalog, so don't offer it. Catalog models
  // only reach the models list at all for a catalog manager.
  const baseAvailableModels = useMemo(() => {
    const eligible = models.filter(
      (m) =>
        m.model_id !== currentModelId &&
        (currentModelIsCatalog
          ? m.is_catalog
          : m.relation === "Owner" || m.is_catalog),
    );
    return [
      ...eligible.filter((m) => !m.is_catalog),
      ...eligible.filter((m) => m.is_catalog),
    ];
  }, [models, currentModelId, currentModelIsCatalog]);

  // Entering the catalog crosses into role-owned, publishable space, so it needs
  // the parent-lock gate that a move between the caller's own models doesn't.
  const catalogBlocked = !currentModelIsCatalog && !canEnterCatalog;

  // Server-judged eligibility per destination (D42's org boundary, the sealed
  // wall, catalog direction, parent-lock). Fetched for the visible candidate set
  // once, so every row can name its own refusal instead of failing on click.
  useEffect(() => {
    if (!open || baseAvailableModels.length === 0) {
      setEligibility({});
      setEligibilityPending(false);
      return;
    }
    let cancelled = false;
    // Held rather than rendered-then-corrected: a refused destination can be
    // hidden outright, and a row that appears and then vanishes reads as a bug.
    setEligibilityPending(true);
    fetchTransferDestinations(
      tableId,
      baseAvailableModels.map((m) => m.model_id),
    )
      .then((rows) => {
        if (cancelled) return;
        setEligibility(
          Object.fromEntries(rows.map((row) => [row.model_id, row])),
        );
      })
      .catch((error) => {
        // Leave the map empty: rows stay enabled and the door refuses on click,
        // which is the pre-D42 behaviour rather than a dialog that offers
        // nothing.
        log.error("Failed to fetch transfer destinations", error);
      })
      .finally(() => {
        if (!cancelled) setEligibilityPending(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, tableId, baseAvailableModels]);

  const offerableModels = useMemo(() => {
    return baseAvailableModels.filter((m) => {
      const reason = eligibility[m.model_id]?.reason;
      return !reason || !HIDDEN_REFUSALS.has(reason);
    });
  }, [baseAvailableModels, eligibility]);

  const filteredModels = useMemo(() => {
    if (!searchQuery.trim()) return offerableModels;
    const q = searchQuery.toLowerCase();
    return offerableModels.filter((m) => m.name.toLowerCase().includes(q));
  }, [offerableModels, searchQuery]);

  // The import door refuses the reverse move, so a detaching transfer can't be
  // walked back from here — it asks first.
  const requestTransfer = (model: ModelElementType) => {
    if (eligibility[model.model_id]?.detaches_source) {
      setPendingDetach(model);
      return;
    }
    void handleTransfer(model.model_id);
  };

  const confirmPendingDetach = () => {
    const target = pendingDetach;
    setPendingDetach(null);
    if (target) void handleTransfer(target.model_id);
  };

  const handleTransfer = async (targetModelId: string) => {
    if (transferringModelId || !currentModelId) return;

    setSuccessModelIds((prev) => removeFromSet(prev, targetModelId));
    setFailedModelIds((prev) => removeFromSet(prev, targetModelId));

    setTransferringModelId(targetModelId);
    try {
      const result = await handleTableOwnershipTransfer(
        tableId,
        currentModelId,
        targetModelId,
      );
      if (result.success) {
        setSuccessModelIds((prev) => addToSet(prev, targetModelId));
        showNotificationGlobal(
          `Table "${tableName}" ownership transferred successfully`,
        );
        timersRef.current.push(setTimeout(() => onClose(), 1500));
      } else {
        setFailedModelIds((prev) => addToSet(prev, targetModelId));
        timersRef.current.push(
          setTimeout(
            () =>
              setFailedModelIds((prev) => removeFromSet(prev, targetModelId)),
            3000,
          ),
        );
        showNotificationGlobal(
          `Failed to transfer table "${tableName}"`,
          "error",
        );
      }
    } catch (error) {
      log.error("Error transferring ownership", error);
      setFailedModelIds((prev) => addToSet(prev, targetModelId));
      timersRef.current.push(
        setTimeout(
          () => setFailedModelIds((prev) => removeFromSet(prev, targetModelId)),
          3000,
        ),
      );
      showNotificationGlobal(
        `Failed to transfer table "${tableName}"`,
        "error",
      );
    } finally {
      setTransferringModelId(null);
    }
  };

  return (
    <>
      <DeleteDialogSection
        className="border-0"
        headerContent={
          offerableModels.length > 0 ? (
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search models..."
            />
          ) : undefined
        }
      >
        <div className="min-h-12.5 max-h-[50vh] overflow-y-auto">
          {isLoading || eligibilityPending ? (
            <div className="flex justify-center items-center py-8">
              <Spinner />
            </div>
          ) : (
            <>
              {filteredModels.map((model) => {
                const isCatalog = model.is_catalog === true;
                const verdict = eligibility[model.model_id];
                const refusal =
                  verdict && !verdict.can_receive ? verdict.reason : undefined;
                const blocked = (isCatalog && catalogBlocked) || !!refusal;
                return (
                  <DeleteDialogItem key={model.model_id} className="group">
                    <div className="flex items-center gap-2 w-full px-2">
                      <AssetIcon
                        size="sm"
                        color={model.color}
                        iconIndex={model.icon}
                        className="w-3.5! h-3.5!"
                      />
                      <span
                        className={`flex-1 cursor-default select-none truncate ${
                          blocked ? "text-fg-muted" : "text-fg-medium"
                        }`}
                      >
                        {model.name || "Unnamed Model"}
                      </span>
                      {isCatalog && (
                        <StatusChip tone="warning">Catalog</StatusChip>
                      )}
                      <ActionStatus
                        isLoading={transferringModelId === model.model_id}
                        isSuccess={successModelIds.has(model.model_id)}
                        isFailed={failedModelIds.has(model.model_id)}
                        onAction={() => requestTransfer(model)}
                        disabled={!!transferringModelId || blocked}
                        title={
                          refusal
                            ? REFUSAL_COPY[refusal]
                            : blocked
                              ? "External-source tables can't be moved into the catalog"
                              : "Move table here"
                        }
                      />
                    </div>
                  </DeleteDialogItem>
                );
              })}
              {filteredModels.length === 0 && (
                <div className="text-fg-muted-regular py-4 text-center">
                  {searchQuery.trim()
                    ? "No models match your search."
                    : currentModelIsCatalog
                      ? "No other catalog models available"
                      : "No models available for transfer"}
                </div>
              )}
            </>
          )}
        </div>
      </DeleteDialogSection>

      <ConfirmDialog
        open={pendingDetach !== null}
        title="Move out of this model?"
        message={
          <Stack gap="xs">
            <p>
              <span className="text-fg-bold">{tableName}</span> moves to{" "}
              <span className="text-fg-bold">
                {pendingDetach?.name || "Unnamed Model"}
              </span>{" "}
              and leaves this model.
            </p>
            <p className="text-small text-fg-muted">
              It won&apos;t stay behind as an imported table, and its views on
              this canvas go with it — the destination belongs to another
              organization, whose tables can&apos;t be shared back out.
            </p>
          </Stack>
        }
        confirmText="Move table"
        confirmVariant="destructive"
        onConfirm={confirmPendingDetach}
        onCancel={() => setPendingDetach(null)}
      />
    </>
  );
};
